import { useEffect } from 'react'

// ═══════════════════════════════════════════════════════════════════════════
// DASHBOARD MANIFEST SWAP
//
// The problem this solves: on iPhone, web push only works for a site that has
// been added to the home screen, and the site manifest (public/manifest.json)
// is scoped to "/portal" for the member app. The dashboard lives at /dashboard,
// outside that scope, so it cannot be installed as its own app.
//
// The fix: while the user is on a /dashboard route, point the manifest link at
// public/dashboard-manifest.json, which is scoped to "/dashboard". Safari reads
// the manifest from the DOM at the moment you tap Share > Add to Home Screen,
// so this is all that is required.
//
// What this deliberately does NOT do:
//   - it does not edit public/manifest.json
//   - it does not register a second service worker
//   - it does not run on any non-dashboard route
// The member "My Programs" app is untouched. iOS keeps each home-screen web app
// in its own container, so the two icons hold separate logins and separate push
// registrations.
//
// The original href is captured and restored on unmount, so a client-side
// navigation from /dashboard back to a public page leaves the DOM exactly as it
// was found.
// ═══════════════════════════════════════════════════════════════════════════

const DASHBOARD_MANIFEST = '/dashboard-manifest.json'

export function useDashboardManifest(): void {
  useEffect(() => {
    if (typeof document === 'undefined') return

    const link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]')
    if (!link) return

    const original = link.getAttribute('href')
    if (original === DASHBOARD_MANIFEST) return

    link.setAttribute('href', DASHBOARD_MANIFEST)

    return () => {
      if (original === null) {
        link.removeAttribute('href')
      } else {
        link.setAttribute('href', original)
      }
    }
  }, [])
}
